import { DEFAULT_SITE_ORIGIN, absolutePublicAsset, siteOrigin } from '@/config/envLinks';
import { content } from '@/content/sr';

const { company } = content;

/** Jedno mesto za sve što se ponavlja u <head> — menja se ovde, primenjuje svuda. */
export const SEO = {
  siteName: company.name,
  legalName: company.fullName,
  /** Dodaje se na kraj svakog <title> osim naslovne. */
  titleSuffix: company.name,
  /** Google seče title oko 60 znakova, description oko 155. */
  maxTitle: 60,
  maxDescription: 160,
  locale: 'sr_RS',
  lang: 'sr-RS',
  defaultImage: '/og-walllux-v2.png',
  defaultImageAlt: `${company.fullName} — ${company.tagline}`,
  logo: '/logo-512.png',
};

/** Apsolutna kanonska URL za putanju (`/o-nama`, `/shop/panels`, …). */
export function canonicalUrl(path = '/') {
  const base = siteOrigin() || DEFAULT_SITE_ORIGIN;
  if (!path || path === '/') return `${base}/`;
  const p = path.startsWith('/') ? path : `/${path}`;
  // Bez završne kose crte — sitemap i kanonske URL moraju da budu isti string.
  return `${base}${p.replace(/\/$/, '')}`;
}

/** Slika za deljenje: apsolutna public putanja ili već apsolutan URL (npr. slika proizvoda). */
export function shareImage(image) {
  if (!image) return absolutePublicAsset(SEO.defaultImage);
  return /^https?:\/\//i.test(image) ? image : absolutePublicAsset(image);
}

/** Skraćuje opis na granici reči da rich snippet ne bi bio presečen nasred reči. */
export function clampDescription(text, max = SEO.maxDescription) {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,.;:–-]$/, '')}…`;
}

/** Referenca na firmu iz index.html grafa — vezuje šeme stranica za isti entitet. */
export const BUSINESS_REF = { '@id': `${DEFAULT_SITE_ORIGIN}/#business` };

/**
 * Mrvice za pretragu.
 * @param {Array<{name: string, path: string}>} trail bez „Početna" — dodaje se automatski
 */
export function breadcrumbJsonLd(trail = []) {
  const items = [{ name: 'Početna', path: '/' }, ...trail];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

/**
 * Product šema — uslov za rich rezultat sa cenom u Google pretrazi.
 * @param {object} product proizvod sa backenda
 * @param {object} opts    { url, images: string[] (apsolutne), price }
 */
export function productJsonLd(product, { url, images = [], price }) {
  const variants = product.variants || [];
  // Proizvod bez varijanti nema podatak o zalihama — tretira se kao raspoloživ,
  // isto kao u UI (dugme „Dodaj u korpu" je uvek aktivno).
  const inStock = variants.length === 0 || variants.some((v) => v.inStock);
  const availability = inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const prices = variants.map((v) => Number(v.price)).filter(Number.isFinite);
  const offers =
    prices.length > 1
      ? {
          '@type': 'AggregateOffer',
          priceCurrency: 'RSD',
          lowPrice: Math.min(...prices),
          highPrice: Math.max(...prices),
          offerCount: prices.length,
          availability,
          url,
          seller: BUSINESS_REF,
        }
      : {
          '@type': 'Offer',
          priceCurrency: 'RSD',
          price: Number(price),
          availability,
          url,
          seller: BUSINESS_REF,
        };

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    url,
    ...(product.description ? { description: clampDescription(product.description, 500) } : {}),
    ...(images.length ? { image: images } : {}),
    ...(product.categoryName ? { category: product.categoryName } : {}),
    sku: product.slug,
    brand: { '@type': 'Brand', name: SEO.siteName },
    ...(Number.isFinite(Number(price)) ? { offers } : {}),
  };
}
